import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Sidebar.css';
import { useAuthStore } from '../store/authStore';
import { hasModuleAccess } from '../utils/moduleAccess';
import {
  DashboardIcon,
  SealsIcon,
  CompaniesIcon,
  UsersIcon,
  DepotsIcon,
  BoxAssignmentsIcon,
  ContainerAssignIcon,
  NotificationsIcon,
  ReportsIcon,
  AuditLogsIcon,
  ManagerIcon,
  SettingsIcon,
  LogoutIcon,
  DropdownIcon,
} from './Icons';

function Sidebar({ isOpen, onClose }) {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const permissions = useAuthStore((state) => state.permissions);
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  // Close profile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    }

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showProfileMenu]);

  // Close sidebar on mobile when clicking a link
  const handleNavClick = () => {
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  // Helper function to check if a link is active
  const isActive = (paths) => {
    const pathsArray = Array.isArray(paths) ? paths : [paths];
    // Exact match or starts with for subpaths
    return pathsArray.some(path => {
      if (path === '/') return location.pathname === '/';
      return location.pathname === path || location.pathname.startsWith(path + '/');
    });
  };

  // Helper function to render nav item only if user has access
  const renderNavItem = (moduleKey, label, paths, icon = null) => {
    const isAdminLike = user?.user_type === 'gods_eye' || user?.user_type === 'manager';
    if (user && !isAdminLike && !hasModuleAccess(permissions, moduleKey)) {
      return null;
    }

    const isLinkActive = isActive(paths);
    const path = Array.isArray(paths) ? paths[0] : paths;
    
    return (
      <li className="sidebar-nav-item" key={moduleKey}>
        <Link 
          to={path}
          className={`sidebar-nav-link ${isLinkActive ? 'active' : ''}`}
          onClick={handleNavClick}
        >
          <span className="nav-icon">{icon}</span>
          <span className="nav-label">{label}</span>
        </Link>
      </li>
    );
  };

  const handleLogout = () => {
    setShowProfileMenu(false);
    logout();
    navigate('/login');
  };

  const handleSettings = () => {
    setShowProfileMenu(false);
    navigate('/settings');
    onClose();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      {/* Sidebar */}
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        {/* Close button for mobile */}
        <button className="sidebar-close-btn" onClick={onClose} aria-label="Close sidebar">
          ✕
        </button>

        {/* Logo section */}
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo" onClick={handleNavClick}>
            <span className="logo-icon">
              <img src="/logo-sealsapp.png" alt="Seals App" className="logo-image" />
            </span>
            <span className="logo-text">Seals</span>
          </Link>
        </div>

        {/* Main navigation */}
        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {renderNavItem('dashboard', 'Dashboard', '/', <DashboardIcon />)}
            {renderNavItem(
              'devices',
              'Seals',
              user?.user_type === 'gods_eye' ? '/devices' : '/devices-view',
              <SealsIcon />
            )}
            {user?.user_type !== 'cliente' &&
              renderNavItem('companies', 'Companies', '/companies', <CompaniesIcon />)}
            {renderNavItem('users', 'Users', '/users', <UsersIcon />)}
            {user?.user_type !== 'operador' && renderNavItem('depots', 'Depots', '/depots', <DepotsIcon />)}
            {renderNavItem(
              'device_assignments',
              'Box Assignments',
              '/box-assignments',
              <BoxAssignmentsIcon />
            )}
            {user?.user_type === 'operador' && (
              <li className="sidebar-nav-item">
                <Link 
                  to="/container-assignment"
                  className="sidebar-nav-link"
                  onClick={handleNavClick}
                >
                  <span className="nav-icon"><ContainerAssignIcon /></span>
                  <span className="nav-label">Assign Containers</span>
                </Link>
              </li>
            )}
            {renderNavItem('notifications', 'Notifications', '/notifications', <NotificationsIcon />)}
            {renderNavItem('reports', 'Reports', '/reports', <ReportsIcon />)}
            {user?.user_type !== 'cliente' &&
              renderNavItem('audit_logs', 'Audit Logs', '/audit-logs', <AuditLogsIcon />)}
            {user?.user_type === 'gods_eye' && (
              <li className="sidebar-nav-item" key="manager_assignments">
                <Link 
                  to="/permissions"
                  className={`sidebar-nav-link ${isActive('/permissions') ? 'active' : ''}`}
                  onClick={handleNavClick}
                >
                  <span className="nav-icon"><ManagerIcon /></span>
                  <span className="nav-label">Manager Assignments</span>
                </Link>
              </li>
            )}
          </ul>
        </nav>

        {/* Profile section at bottom */}
        <div className="sidebar-footer">
          <div className="profile-section" ref={profileMenuRef}>
            <button 
              className="profile-btn"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              aria-label="Profile menu"
            >
              <span className="profile-avatar"><UsersIcon /></span>
              <div className="profile-info">
                <div className="profile-username">{user?.username}</div>
                <div className="profile-role">{user?.user_type}</div>
              </div>
              <span className={`profile-arrow ${showProfileMenu ? 'open' : ''}`}><DropdownIcon /></span>
            </button>

            {showProfileMenu && (
              <div className="profile-menu-dropdown">
                <button 
                  className="profile-menu-item"
                  onClick={handleSettings}
                >
                  <span className="profile-menu-icon"><SettingsIcon /></span>
                  <span>Settings</span>
                </button>
                <div className="profile-menu-divider"></div>
                <button 
                  className="profile-menu-item logout"
                  onClick={handleLogout}
                >
                  <span className="profile-menu-icon"><LogoutIcon /></span>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
